<script setup lang="ts">
import { onMounted, ref, computed, watch } from 'vue'

const plyr = ref(null)
const isVisible = ref(false)

const props = defineProps({
  src_id: {
    type: String,
    required: true,
    default: '-FKa-4NXfic'
  },
  autoplay: {
    type: Boolean,
    default: true
  },
  muted: {
    type: Boolean,
    default: false
  },
  controls: {
    type: Array,
    default: () => ['play-large', 'play', 'progress', 'mute', 'volume', 'fullscreen']
  },
  volume: {
    type: Number,
    default: 0
  },
  clickToPlay: {
    type: Boolean,
    default: true
  },
  resetOnEnd: {
    type: Boolean,
    default: true
  },
  quality: {
    type: Object,
    default: () => ({ default: 1080, options: [1080, 720] })
  },
  // YouTube iframe query parameters
  ivLoadPolicy: {
    type: Number,
    default: 3
  },
  modestbranding: {
    type: Number,
    default: 1
  },
  playsinline: {
    type: Number,
    default: 1
  },
  showinfo: {
    type: Number,
    default: 0
  },
  rel: {
    type: Number,
    default: 0
  },
  enablejsapi: {
    type: Number,
    default: 1
  },
  // iframe attributes
  allowFullscreen: {
    type: Boolean,
    default: true
  },
  allowTransparency: {
    type: Boolean,
    default: true
  },
  allowOptions: {
    type: String,
    default: 'autoplay'
  },
  // Style options
  borderRadius: {
    type: String,
    default: '10px'
  },
  loop: {
    type: Object,
    default: () => ({ active: false })
  },
  fullscreen: {
    type: Object,
    default: () => ({ enabled: true })
  },
  disableContextMenu: {
    type: Boolean,
    default: false
  },
  hideControls: {
    type: Boolean,
    default: false
  },
  settings: {
    type: Array,
    default: () => ['captions', 'quality', 'speed', 'loop']
  },
  // Fade in to hide YouTube branding flash
  fadeIn: {
    type: Boolean,
    default: false
  },
  fadeInDuration: {
    type: Number,
    default: 3700
  },
  ratio: {
    type: String,
    default: '16:9'
  }
})

const options = computed(() => ({
  muted: props.muted,
  autoplay: props.autoplay,
  controls: props.controls,
  volume: props.volume,
  clickToPlay: props.clickToPlay,
  resetOnEnd: props.resetOnEnd,
  quality: props.quality,
  loop: props.loop,
  fullscreen: props.fullscreen,
  disableContextMenu: props.disableContextMenu,
  hideControls: props.hideControls,
  settings: props.settings,
  ratio: props.ratio
}))

const youtubeParams = computed(() => {
  return `?amp;iv_load_policy=${props.ivLoadPolicy}&amp;modestbranding=${props.modestbranding}&amp;playsinline=${props.playsinline}&amp;showinfo=${props.showinfo}&amp;rel=${props.rel}&amp;enablejsapi=${props.enablejsapi}`
})

const playerClasses = computed(() => {
  if (!props.fadeIn) return ''
  return isVisible.value ? 'opacity-100' : 'opacity-0'
})

// Function to immediately show the video when play icon is clicked
const showVideoNow = () => {
  isVisible.value = true
}

onMounted(() => {
  if (plyr.value) {
    plyr.value.player.play()
  }
  
  if (props.fadeIn) {
    setTimeout(() => {
      isVisible.value = true
    }, props.fadeInDuration)
  } else {
    isVisible.value = true
  }
})

</script>

<template>
  <ClientOnly>
    <div v-auto-animate class="relative" :class="$attrs.class" :style="{'border-radius': props.borderRadius}">
      <!-- Skeleton loader with play icon -->
      <div v-auto-animate v-if="props.fadeIn && !isVisible" 
           class="absolute inset-0 bg-muted-foreground/10 rounded flex items-center justify-center cursor-pointer"
           :style="`border-radius: ${props.borderRadius}`"
           @click="showVideoNow">
        <Icon name="material-symbols:play-circle-rounded" class="text-background text-6xl" />
      </div>
      
      <div :class="['select-none transition-opacity duration-1000 ease-in-out', playerClasses]">
        <vue-plyr ref="plyr" :options="options">
          <div class="plyr__video-embed">
            <iframe
              :src="`https://www.youtube.com/embed/${props.src_id}${youtubeParams}`" 
              :allowfullscreen="props.allowFullscreen"
              :allowtransparency="props.allowTransparency"
              :allow="props.allowOptions"/>
          </div>
        </vue-plyr>
      </div>
    </div>
  </ClientOnly>
</template>

<style>
@import '@skjnldsv/vue-plyr/dist/vue-plyr.css';

.plyr__video-wrapper video,
.plyr--video {
  border-radius: v-bind('props.borderRadius') !important;
  padding: 0 !important;
  margin: 0 !important;
}

.plyr__poster {
  border-radius: v-bind('props.borderRadius') !important;
  padding: 0 !important;
  margin: 0 !important;
  background-size: cover !important;
}

:root {
  --plyr-color-main: var(--primary) !important;
}

.plyr__video-embed iframe {
	top: -50%;
	height: 200%;
  background-color: var(--background) !important;
}

</style>
